import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'false', label: 'Em aberto' },
  { value: 'true', label: 'Pagas' }
];

const meses = Array.from({ length: 12 }).map((_, index) => {
  const value = String(index + 1).padStart(2, '0');
  return {
    value,
    label: new Date(0, index).toLocaleDateString('pt-BR', { month: 'long' })
  };
});

const PixModal = ({ cobrança, onClose }) => {
  if (!cobrança?.pix) return null;

  const expiracao = cobrança.pix.expiracao ? new Date(cobrança.pix.expiracao) : null;

  const copyPix = async () => {
    if (!navigator?.clipboard || !cobrança.pix.copiaCola) {
      toast.error('Não foi possível copiar o código PIX automaticamente.');
      return;
    }
    await navigator.clipboard.writeText(cobrança.pix.copiaCola);
    toast.success('Código PIX copiado!');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <button
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          ✕
        </button>
        <div className="grid gap-6 p-6 md:grid-cols-2">
          <div className="flex flex-col items-center justify-center">
            {cobrança.pix.qrCode ? (
              <img
                src={cobrança.pix.qrCode}
                alt="QR Code PIX"
                className="h-64 w-64 rounded-xl border border-gray-200 object-contain"
              />
            ) : (
              <div className="flex h-64 w-64 items-center justify-center rounded-xl border border-dashed border-gray-200 text-center text-sm text-gray-500">
                QR Code indisponível. Use o código copia e cola ao lado.
              </div>
            )}
            {expiracao && (
              <p className="mt-3 text-sm text-amber-600">
                Expira em{' '}
                {expiracao.toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900">Dados do PIX</h3>
            <p className="mt-1 text-sm text-gray-500">
              Use o QR Code ou copie o código abaixo para pagar com PIX.
            </p>
            <div className="mt-4 flex-1 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-600">PIX Copia e Cola</p>
              <textarea
                className="mt-2 h-40 w-full rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700"
                readOnly
                value={cobrança.pix.copiaCola || 'Código copia e cola indisponível.'}
              />
              <button
                onClick={copyPix}
                disabled={!cobrança.pix.copiaCola}
                className="mt-3 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Copiar código
              </button>
            </div>
            <div className="mt-4 rounded-lg bg-primary-50 p-3 text-sm text-primary-800">
              <p>{cobrança.apartamento?.apartamento_bloco} - {cobrança.apartamento?.apartamento_numero}</p>
              <p className="font-semibold text-lg">
                {Number(cobrança.cobranca_valor_atualizado ?? cobrança.cobranca_valor).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                })}
              </p>
              <p className="text-xs text-primary-600">Mês {cobrança.cobranca_mes_referencia}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const dataHojeIso = () => {
  const data = new Date();
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
};

const formatarValorInput = (numero) => Number(numero || 0).toFixed(2).replace('.', ',');

const calcularTotalBaixa = (cobranca, dataIso, jurosPct, multaPct) => {
  const original = Number(cobranca?.cobranca_valor) || 0;
  const vencimento = String(cobranca?.cobranca_data_vencimento || '').slice(0, 10);
  const baixa = String(dataIso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(vencimento) || !/^\d{4}-\d{2}-\d{2}$/.test(baixa) || baixa <= vencimento) {
    return original;
  }
  const dias = Math.round(
    (new Date(`${baixa}T12:00:00`).getTime() - new Date(`${vencimento}T12:00:00`).getTime()) / 86400000
  );
  if (dias <= 0) return original;
  const multa = original * ((Number(multaPct) || 0) / 100);
  const juros = original * ((Number(jurosPct) || 0) / 100) * (dias / 30);
  return Math.round((original + multa + juros) * 100) / 100;
};

const CobrancasCondominio = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioNome, getCondominioId } = useCondominio();

  const allowedRoles = ['administrador', 'sindico', 'subsindico', 'financeiro'];
  const hasAccess = allowedRoles.includes((user?.tipo || '').toLowerCase());

  const condominioId = getCondominioId();

  const hoje = new Date();
  const [filters, setFilters] = useState({
    mes: String(hoje.getMonth() + 1).padStart(2, '0'),
    ano: String(hoje.getFullYear()),
    status: '',
    bloco: '',
    unidade: ''
  });

  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pixModal, setPixModal] = useState(null);
  const [baixaModal, setBaixaModal] = useState(null);
  const [valorBaixa, setValorBaixa] = useState('');
  const [dataBaixa, setDataBaixa] = useState(dataHojeIso);
  const [valorBaixaAlterado, setValorBaixaAlterado] = useState(false);
  const [percentualJuros, setPercentualJuros] = useState(1);
  const [percentualMultas, setPercentualMultas] = useState(2);
  const [salvandoBaixa, setSalvandoBaixa] = useState(false);
  const [gerandoPixId, setGerandoPixId] = useState(null);
  const [showGerarModal, setShowGerarModal] = useState(false);
  const [apartamentos, setApartamentos] = useState([]);
  const [apartamentosSelecionados, setApartamentosSelecionados] = useState([]);
  const [gerarForm, setGerarForm] = useState({
    mes_referencia: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    data_vencimento: ''
  });
  const [diaVencimento, setDiaVencimento] = useState(10);
  const [gerandoCobrancas, setGerandoCobrancas] = useState(false);
  const [blocoGerar, setBlocoGerar] = useState('');
  const [filtroUnidadeGerar, setFiltroUnidadeGerar] = useState('');
  const fetchSeq = useRef(0);

  const resumo = useMemo(() => {
    const total = cobrancas.reduce((acc, item) => acc + Number(item.cobranca_valor || 0), 0);
    const aberto = cobrancas.filter(item => !item.cobranca_pago).length;
    const pagos = cobrancas.filter(item => item.cobranca_pago).length;
    return { total, aberto, pagos };
  }, [cobrancas]);

  const fetchCobrancas = async () => {
    if (!condominioId) return;
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const params = {
        condominio_id: condominioId,
        mes_referencia: filters.mes && filters.ano ? `${filters.ano}-${filters.mes}` : undefined,
        pago: filters.status || undefined,
        bloco: filters.bloco?.trim() || undefined,
        unidade: filters.unidade?.trim() || undefined
      };
      const { data } = await api.get('/cobrancas-condominio', { params });
      if (seq !== fetchSeq.current) return;
      setCobrancas(data?.data?.cobrancas || []);
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      console.error(error);
      toast.error(error.response?.data?.message || 'Erro ao carregar cobranças.');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCobrancas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominioId, filters.mes, filters.ano, filters.status, filters.bloco, filters.unidade]);

  useEffect(() => {
    if (!condominioId) return;
    api.get('/apartamentos', { params: { condominio_id: condominioId, limit: 5000 } })
      .then(({ data }) => {
        setApartamentos(data?.data?.apartamentos || []);
      })
      .catch((error) => {
        console.error(error);
      });
    api.get('/parametros', { params: { condominio_id: condominioId } })
      .then(({ data }) => {
        const lista = data?.data?.parametros || [];
        const juros = parseFloat(lista.find((item) => item.parametro_nome === 'juros')?.parametro_valor);
        const multas = parseFloat(lista.find((item) => item.parametro_nome === 'multas')?.parametro_valor);
        if (Number.isFinite(juros)) setPercentualJuros(juros);
        if (Number.isFinite(multas)) setPercentualMultas(multas);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [condominioId]);

  const blocos = useMemo(() => {
    return [...new Set(apartamentos.map((apt) => apt.apartamento_bloco).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' }));
  }, [apartamentos]);

  const apartamentosVisiveis = useMemo(() => {
    const termo = filtroUnidadeGerar.trim().toLowerCase();
    return apartamentos.filter((apt) => {
      const mesmoBloco = !blocoGerar
        || String(apt.apartamento_bloco || '').toLowerCase() === blocoGerar.toLowerCase();
      if (!mesmoBloco) return false;
      if (!termo) return true;
      const texto = `${apt.apartamento_bloco || ''} ${apt.apartamento_numero || ''}`.toLowerCase();
      return texto.includes(termo);
    });
  }, [apartamentos, blocoGerar, filtroUnidadeGerar]);

  const abrirModalGerar = async () => {
    setBlocoGerar('');
    setFiltroUnidadeGerar('');
    setShowGerarModal(true);
    try {
      const [aptRes, paramRes] = await Promise.all([
        api.get('/apartamentos', { params: { condominio_id: condominioId, limit: 2000 } }),
        api.get('/parametros', { params: { condominio_id: condominioId } })
      ]);
      const apts = aptRes?.data?.data?.apartamentos || [];
      setApartamentos(apts);
      setApartamentosSelecionados(apts.map(a => a.apartamento_id));
      const params = paramRes?.data?.data?.parametros || [];
      const paramVenc = params.find(p => p.parametro_nome === 'data_vencimento');
      const dia = paramVenc ? parseInt(paramVenc.parametro_valor, 10) : 10;
      setDiaVencimento(dia);
      const [ano, mes] = gerarForm.mes_referencia.split('-');
      const dataVen = new Date(parseInt(ano), parseInt(mes) - 1, dia);
      setGerarForm(prev => ({
        ...prev,
        data_vencimento: dataVen.toISOString().split('T')[0]
      }));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar apartamentos');
    }
  };

  const handleGerarCobrancas = async (e) => {
    e.preventDefault();
    if (apartamentosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um apartamento');
      return;
    }
    const [ano, mes] = gerarForm.mes_referencia.split('-');
    const dataVen = gerarForm.data_vencimento || new Date(parseInt(ano), parseInt(mes) - 1, diaVencimento).toISOString().split('T')[0];
    setGerandoCobrancas(true);
    try {
      const { data } = await api.post('/cobrancas-condominio/gerar-recorrentes', {
        condominio_id: condominioId,
        mes_referencia: gerarForm.mes_referencia,
        apartamentos_ids: apartamentosSelecionados,
        data_vencimento: dataVen,
        sobrescrever: true
      });
      setShowGerarModal(false);
      fetchCobrancas();
      const criadas = data?.data?.criadas || 0;
      const removidas = data?.data?.removidas || 0;
      toast.success(`Cobranças geradas! ${criadas} criadas${removidas > 0 ? `, ${removidas} removidas` : ''}.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao gerar cobranças');
    } finally {
      setGerandoCobrancas(false);
    }
  };

  const parseValorBaixa = (texto) => {
    if (texto == null || texto === '') return 0;
    const normalizado = String(texto).replace(/\s|R\$/g, '').trim();
    if (normalizado.includes(',')) {
      return parseFloat(normalizado.replace(/\./g, '').replace(',', '.')) || 0;
    }
    return parseFloat(normalizado) || 0;
  };

  const handleSalvarBaixa = async () => {
    if (!baixaModal) return;
    const valor = parseValorBaixa(valorBaixa);
    if (valor <= 0) {
      toast.error('Informe um valor de baixa maior que zero');
      return;
    }
    setSalvandoBaixa(true);
    try {
      await api.put(`/cobrancas-condominio/${baixaModal.cobranca_id}/baixa`, {
        cobranca_valor_baixa: valor,
        cobranca_pago: true,
        cobranca_data_baixa: dataBaixa
      });
      toast.success('Baixa registrada com sucesso');
      setBaixaModal(null);
      fetchCobrancas();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao registrar baixa');
    } finally {
      setSalvandoBaixa(false);
    }
  };

  const handleExcluirBaixa = async (cobranca) => {
    if (!window.confirm('Excluir a baixa desta cobrança? O lançamento financeiro será removido.')) return;
    try {
      await api.put(`/cobrancas-condominio/${cobranca.cobranca_id}/baixa`, {
        cobranca_valor_baixa: cobranca.cobranca_valor_baixa || cobranca.cobranca_valor || 0,
        cobranca_pago: false
      });
      toast.success('Baixa excluída');
      fetchCobrancas();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao excluir baixa');
    }
  };

  const toggleApartamento = (id) => {
    setApartamentosSelecionados(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selecionarBlocoGerar = (bloco) => {
    setBlocoGerar(bloco);
    if (!bloco) {
      setApartamentosSelecionados(apartamentos.map((apt) => apt.apartamento_id));
      return;
    }
    setApartamentosSelecionados(
      apartamentos
        .filter((apt) => String(apt.apartamento_bloco || '').toLowerCase() === bloco.toLowerCase())
        .map((apt) => apt.apartamento_id)
    );
  };

  const selecionarTodos = () => {
    const idsVisiveis = apartamentosVisiveis.map((apt) => apt.apartamento_id);
    const todosMarcados = idsVisiveis.length > 0 && idsVisiveis.every((id) => apartamentosSelecionados.includes(id));
    if (todosMarcados) {
      setApartamentosSelecionados((prev) => prev.filter((id) => !idsVisiveis.includes(id)));
    } else {
      setApartamentosSelecionados((prev) => [...new Set([...prev, ...idsVisiveis])]);
    }
  };

  const handleGerarPix = async (cobrancaId, regerar = false) => {
    try {
      setGerandoPixId(cobrancaId);
      const { data } = await api.post(`/cobrancas-condominio/${cobrancaId}/gerar-pix`, {
        regerar
      });
      const cobrancaAtualizada = data?.data?.cobranca;
      if (cobrancaAtualizada) {
        setCobrancas(prev =>
          prev.map(c => (c.cobranca_id === cobrancaAtualizada.cobranca_id ? cobrancaAtualizada : c))
        );
        setPixModal(cobrancaAtualizada);
      }
      toast.success('PIX gerado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Erro ao gerar PIX.');
    } finally {
      setGerandoPixId(null);
    }
  };

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (value) => {
    if (!value) return '—';
    if (typeof value === 'string' && value.includes('T')) {
      return new Date(value).toLocaleDateString('pt-BR');
    }
    if (typeof value === 'string') {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }
    return new Date(value).toLocaleDateString('pt-BR');
  };

  if (!hasAccess) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <p className="text-gray-700">
          Apenas administradores, síndicos, subsíndicos ou usuários financeiros podem acessar esta tela.
        </p>
      </div>
    );
  }

  if (!condominioId) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <p className="text-gray-700">Selecione um condomínio para visualizar as cobranças.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">Condomínio atual</p>
            <h1 className="text-2xl font-semibold text-gray-900">{getCondominioNome()}</h1>
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-primary"
              onClick={abrirModalGerar}
            >
              <Plus className="h-4 w-4 mr-2" />
              Gerar Cobranças
            </button>
            <button
              className="rounded-lg border border-primary-200 px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
              onClick={fetchCobrancas}
            >
              Atualizar
            </button>
          </div>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Cobranças em aberto</p>
            <p className="text-2xl font-semibold text-red-600">{resumo.aberto}</p>
          </div>
          <div className="rounded-2xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Cobranças pagas</p>
            <p className="text-2xl font-semibold text-green-600">{resumo.pagos}</p>
          </div>
          <div className="rounded-2xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Valor total listado</p>
            <p className="text-2xl font-semibold text-gray-900">{formatCurrency(resumo.total)}</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="text-sm font-medium text-gray-700">Mês</label>
            <select
              value={filters.mes}
              onChange={(event) => setFilters((prev) => ({ ...prev, mes: event.target.value }))}
              className="mt-1 w-full rounded-lg border-gray-200"
            >
              {meses.map((mes) => (
                <option key={mes.value} value={mes.value}>
                  {mes.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Ano</label>
            <input
              type="number"
              value={filters.ano}
              onChange={(event) => {
                const value = event.target.value.slice(0, 4);
                setFilters((prev) => ({ ...prev, ano: value }));
              }}
              className="mt-1 w-full rounded-lg border-gray-200"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              className="mt-1 w-full rounded-lg border-gray-200"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Bloco</label>
            <select
              value={filters.bloco}
              onChange={(event) => setFilters((prev) => ({ ...prev, bloco: event.target.value }))}
              className="mt-1 w-full rounded-lg border-gray-200"
            >
              <option value="">Todos</option>
              {blocos.map((bloco) => (
                <option key={bloco} value={bloco}>{bloco}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Unidade</label>
            <input
              placeholder="Número"
              className="mt-1 w-full rounded-lg border-gray-200"
              value={filters.unidade}
              onChange={(event) => setFilters((prev) => ({ ...prev, unidade: event.target.value }))}
            />
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Cobranças</h2>
          {loading && <LoadingSpinner size="sm" />}
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Unidade</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Data da baixa</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Mês ref.</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Vencimento</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Valor</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">PIX</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cobrancas.length === 0 && !loading && (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={8}>
                    Nenhuma cobrança encontrada com os filtros atuais.
                  </td>
                </tr>
              )}
              {cobrancas.map((cobranca) => {
                const valorAtualizado =
                  cobranca.cobranca_valor_atualizado || cobranca.cobranca_valor;
                const podeGerarPix = !cobranca.cobranca_pago;
                const pixValido =
                  cobranca.pix_qrcode_base64 &&
                  (!cobranca.pix_expiracao || new Date(cobranca.pix_expiracao) > new Date());

                return (
                  <tr key={cobranca.cobranca_id} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                      {cobranca.apartamento?.apartamento_bloco} -{' '}
                      {cobranca.apartamento?.apartamento_numero}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {cobranca.cobranca_data_baixa
                        ? formatDate(cobranca.cobranca_data_baixa)
                        : '—'}
                    </td>
                    <td className="px-4 py-3">{cobranca.cobranca_mes_referencia || '—'}</td>
                    <td className="px-4 py-3">{formatDate(cobranca.cobranca_data_vencimento)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{formatCurrency(valorAtualizado)}</div>
                      {valorAtualizado !== cobranca.cobranca_valor && (
                        <div className="text-xs text-gray-500">
                          Original {formatCurrency(cobranca.cobranca_valor)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {cobranca.cobranca_pago ? (
                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                          Pago
                        </span>
                      ) : (
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                          Em aberto
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {pixValido ? (
                        <button
                          className="text-sm font-semibold text-primary-700 hover:text-primary-900"
                          onClick={() =>
                            setPixModal({
                              ...cobranca,
                              pix: {
                                qrCode: cobranca.pix_qrcode_base64,
                                copiaCola: cobranca.pix_copia_cola,
                                expiracao: cobranca.pix_expiracao
                              }
                            })
                          }
                        >
                          Ver QR Code
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">PIX não gerado</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {!cobranca.cobranca_pago ? (
                        <button
                          className="rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700"
                          onClick={() => {
                            const data = dataHojeIso();
                            const total = calcularTotalBaixa(cobranca, data, percentualJuros, percentualMultas);
                            setDataBaixa(data);
                            setValorBaixaAlterado(false);
                            setValorBaixa(formatarValorInput(total));
                            setBaixaModal(cobranca);
                          }}
                        >
                          Dar baixa
                        </button>
                      ) : (
                        <button
                          className="rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
                          onClick={() => handleExcluirBaixa(cobranca)}
                        >
                          Excluir baixa
                        </button>
                      )}
                      <button
                        className="rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-40"
                        disabled={!podeGerarPix || gerandoPixId === cobranca.cobranca_id}
                        onClick={() => handleGerarPix(cobranca.cobranca_id)}
                      >
                        {gerandoPixId === cobranca.cobranca_id ? 'Gerando...' : 'Gerar PIX'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {pixModal && (
        <PixModal
          cobrança={{
            ...pixModal,
            pix: pixModal.pix || {
              qrCode: pixModal.pix_qrcode_base64,
              copiaCola: pixModal.pix_copia_cola,
              expiracao: pixModal.pix_expiracao
            }
          }}
          onClose={() => setPixModal(null)}
        />
      )}

      {baixaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Dar baixa</h2>
              <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => setBaixaModal(null)}>✕</button>
            </div>
            <p className="text-sm text-gray-600">
              {baixaModal.apartamento?.apartamento_bloco} - {baixaModal.apartamento?.apartamento_numero}
              {' · '}Mês {baixaModal.cobranca_mes_referencia || '—'}
            </p>
            <label className="mt-4 block text-sm font-medium text-gray-700">Data da baixa</label>
            <input
              type="date"
              value={dataBaixa}
              onChange={(e) => {
                const novaData = e.target.value;
                setDataBaixa(novaData);
                const total = calcularTotalBaixa(baixaModal, novaData, percentualJuros, percentualMultas);
                if (!valorBaixaAlterado) setValorBaixa(formatarValorInput(total));
              }}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            <label className="mt-4 block text-sm font-medium text-gray-700">Valor total calculado</label>
            <input
              type="text"
              readOnly
              value={formatCurrency(calcularTotalBaixa(baixaModal, dataBaixa, percentualJuros, percentualMultas))}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700"
            />
            <p className="mt-1 text-xs text-gray-500">Calculado com juros e multa até a data da baixa.</p>
            <label className="mt-4 block text-sm font-medium text-gray-700">Valor da baixa</label>
            <input
              type="text"
              inputMode="decimal"
              value={valorBaixa}
              onChange={(e) => {
                setValorBaixaAlterado(true);
                setValorBaixa(e.target.value);
              }}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm text-gray-600" onClick={() => setBaixaModal(null)}>
                Cancelar
              </button>
              <button
                type="button"
                disabled={salvandoBaixa}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                onClick={handleSalvarBaixa}
              >
                {salvandoBaixa ? 'Salvando...' : 'Confirmar baixa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gerar Cobranças */}
      {showGerarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Gerar Cobranças Recorrentes</h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowGerarModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleGerarCobrancas} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mês de Referência</label>
                <input
                  type="month"
                  value={gerarForm.mes_referencia}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGerarForm(prev => ({ ...prev, mes_referencia: val }));
                    const [ano, mes] = val.split('-');
                    const dataVen = new Date(parseInt(ano), parseInt(mes) - 1, diaVencimento);
                    setGerarForm(prev2 => ({ ...prev2, data_vencimento: dataVen.toISOString().split('T')[0] }));
                  }}
                  className="w-full rounded-lg border-gray-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Vencimento</label>
                <input
                  type="date"
                  value={gerarForm.data_vencimento}
                  onChange={(e) => setGerarForm(prev => ({ ...prev, data_vencimento: e.target.value }))}
                  className="w-full rounded-lg border-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">Padrão: dia {diaVencimento} (parâmetros)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bloco</label>
                <select
                  value={blocoGerar}
                  onChange={(e) => selecionarBlocoGerar(e.target.value)}
                  className="w-full rounded-lg border-gray-300"
                >
                  <option value="">Todos os blocos</option>
                  {blocos.map((bloco) => (
                    <option key={bloco} value={bloco}>{bloco}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar unidade</label>
                <input
                  type="text"
                  value={filtroUnidadeGerar}
                  onChange={(e) => setFiltroUnidadeGerar(e.target.value)}
                  placeholder="Bloco ou número, ex.: Suriname"
                  className="w-full rounded-lg border-gray-300"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Apartamentos ({apartamentosVisiveis.length})
                  </label>
                  <button
                    type="button"
                    onClick={selecionarTodos}
                    className="text-sm text-primary-600 hover:text-primary-800"
                  >
                    {apartamentosVisiveis.length > 0 && apartamentosVisiveis.every((apt) => apartamentosSelecionados.includes(apt.apartamento_id))
                      ? 'Desmarcar visíveis'
                      : 'Selecionar visíveis'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  {apartamentosVisiveis.length === 0 && (
                    <p className="p-2 text-sm text-gray-500">Nenhuma unidade encontrada para este filtro.</p>
                  )}
                  {apartamentosVisiveis.map((apt) => (
                    <label key={apt.apartamento_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={apartamentosSelecionados.includes(apt.apartamento_id)}
                        onChange={() => toggleApartamento(apt.apartamento_id)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{apt.apartamento_bloco} - {apt.apartamento_numero}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowGerarModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={gerandoCobrancas}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {gerandoCobrancas ? 'Gerando...' : 'Gerar Cobranças'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CobrancasCondominio;


